import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import useMedia from 'common/hooks/useMedia'
import { formatThaiShortDateTwoDigitYear, getAgeOnDate } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { labelWithSpecies } from 'common/utils/species'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'
import { useGetCycleById } from 'modules/pond/api/queries'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import ChildPondChip from 'modules/pond/components/ChildPondChip'
import { SpeciesName } from 'modules/pond/constants'
import { useMutateDeleteShrimpTransfer } from 'modules/shrimpTransfer/api/mutation'
import { useGetShrimpTransfer } from 'modules/shrimpTransfer/api/queries'
import { PartialTransfer } from 'modules/shrimpTransfer/api/types'
import TransferIcon from 'modules/shrimpTransfer/components/TransferIcon'
import { isCrossFarmTransfer } from 'modules/shrimpTransfer/utils/transferEstimate'
import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CallMadeIcon from '@mui/icons-material/CallMade'
import CallReceivedIcon from '@mui/icons-material/CallReceived'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Box, ButtonBase, Chip, Divider, Stack, Typography } from '@mui/material'

import ShrimpTransferViewDialog from '../ShrimpTransferViewDialog'

interface StatProps {
  icon: ReactNode
  label: string
  value: string
  unit: string
  area: string
}

const Stat = ({ icon, label, value, unit, area }: StatProps) => (
  <Box sx={{ gridArea: area, minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
      {icon}
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
    </Stack>
    <Typography fontWeight={700} component="span">
      {value}
    </Typography>{' '}
    <Typography variant="body2" color="text.secondary" component="span">
      {unit}
    </Typography>
  </Box>
)

const ShrimpTransfersRecordBody = () => {
  const { data } = useGetShrimpTransfer()
  const { data: cycle } = useGetCycleById()
  const isMobile = useMedia('sm')
  const { t } = useTranslation('shrimpTransfer')
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<PartialTransfer | null>(null)
  const [deletingRecord, setDeletingRecord] = useState<PartialTransfer | null>(null)
  const invalidateCostQueries = useInvalidateCostQueries()
  const deleteMutation = useMutateDeleteShrimpTransfer()

  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    title: labelWithSpecies(t('transfer.deleteTitle'), species),
    content: t('transfer.deleteContent', {
      pond: deletingRecord?.destination_pond_name ?? '',
      farm: deletingRecord?.destination_farm_name ?? '',
    }),
    onConfirm: async () => {
      if (!deletingRecord) return
      await deleteMutation.mutateAsync({
        partialTransferId: String(deletingRecord.id),
        destinationFarmId: deletingRecord.destination_farm_id,
        destinationPondId: deletingRecord.destination_pond_id,
      })
      invalidateCostQueries()
    },
  })

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSelectedRecord(null)
  }

  const handleOpenDeleteConfirm = () => {
    setDeletingRecord(selectedRecord)
    openConfirmDialog()
    handleDialogClose()
  }

  const handleRowClick = (item: PartialTransfer) => {
    setSelectedRecord(item)
    setDialogOpen(true)
  }

  const sizeUnit = t(species === SpeciesName.Fish ? 'transfer.gram' : 'transfer.amountPerKg')
  const costUnit = t(isFish ? 'transfer.bahtPerPiece' : 'transfer.bahtPerKg')

  const renderFarmName = (name?: string) => {
    if (!name) return null
    const label = t('transfer.farmLabel', { name })
    return (
      <Typography variant="body2" color="text.secondary" noWrap title={label}>
        {label}
      </Typography>
    )
  }

  return (
    <Stack p={isMobile ? 0 : 2} pt={isMobile ? 2 : 0}>
      {!data || data.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" height="60vh" spacing={2}>
          <Box>
            <img
              src="/static/components/transfersEmpty.png"
              alt="empty"
              width={220}
              height={160}
              style={{ objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {t('transfer.emptyTransferLine1', { species })}
            <br />
            {t('transfer.emptyTransferLine2', { species })}
            <br />
            {t('transfer.emptyTransferLine3', { species })}
          </Typography>
        </Stack>
      ) : (
        <Box>
          {data.map((item) => {
            const isOut = item.type === 'out'
            const crossFarm = isCrossFarmTransfer(item)
            const counterpartPond = t('transfer.pondLabel', {
              name: isOut ? item.destination_pond_name : item.source_pond_name,
            })
            const age = getAgeOnDate(cycle?.startDate, item.transfer_date) ?? item.age
            return (
              <Box key={item.id}>
                {/* ButtonBase gives the row button semantics + Enter/Space handling. */}
                <ButtonBase
                  component="div"
                  onClick={() => handleRowClick(item)}
                  sx={(theme) => ({
                    display: 'grid',
                    width: '100%',
                    textAlign: 'left',
                    alignItems: 'center',
                    columnGap: 2,
                    rowGap: 1.5,
                    px: { xs: 0, md: 1 },
                    py: 2,
                    '&:hover': { bgcolor: 'action.hover' },
                    gridTemplateColumns: {
                      xs: 'repeat(3, minmax(0, 1fr))',
                      md: [
                        theme.spacing(26),
                        // Capped so จาก/ไป stay close together on wide screens; leftover space goes to the spacer before weight
                        `minmax(0, ${theme.spacing(60)})`,
                        theme.spacing(12),
                        theme.spacing(14),
                        theme.spacing(18),
                        'minmax(0, 1fr)',
                        theme.spacing(14),
                        theme.spacing(3),
                      ].join(' '),
                    },
                    gridTemplateAreas: {
                      xs: '"head head weight" "route route route" "age size cost"',
                      md: '"head route age size cost . weight chevron"',
                    },
                  })}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ gridArea: 'head', minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: (theme) => theme.spacing(5.5),
                        height: (theme) => theme.spacing(5.5),
                        flexShrink: 0,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isOut ? 'status.dangerSoft' : 'status.okSoft',
                        color: isOut ? 'status.danger' : 'status.ok',
                      }}
                    >
                      {isOut ? <CallMadeIcon /> : <CallReceivedIcon />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                        <Typography fontWeight={700}>
                          {isOut ? t('transfer.moveOut') : t('transfer.moveIn')}
                        </Typography>
                        {crossFarm && (
                          <Chip
                            size="small"
                            label={t('transfer.crossFarm')}
                            sx={{
                              bgcolor: 'status.infoSoft',
                              color: 'status.info',
                              fontWeight: 600,
                              borderRadius: 0.5,
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {formatThaiShortDateTwoDigitYear(item.transfer_date, { showTime: true })}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Only the other side of the transfer: ไป for a move-out, จาก for a move-in. */}
                  <Box sx={{ gridArea: 'route', minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {isOut ? t('transfer.toShort') : t('transfer.fromShort')}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Typography
                        fontWeight={700}
                        noWrap
                        title={counterpartPond}
                        sx={{ minWidth: 0, flexShrink: 0 }}
                      >
                        {counterpartPond}
                      </Typography>
                      {isOut && item.destination_is_child_pond && <ChildPondChip />}
                      {crossFarm &&
                        renderFarmName(isOut ? item.destination_farm_name : item.source_farm_name)}
                    </Stack>
                  </Box>

                  <Stat
                    area="age"
                    icon={<ScheduleIcon fontSize="inherit" />}
                    label={t('transfer.pondAge', { species })}
                    value={age ? String(age) : '-'}
                    unit={t('transfer.dateUnit')}
                  />
                  <Stat
                    area="size"
                    icon={<TransferIcon name="ruler" size={2} />}
                    label={t('transfer.sizeShort')}
                    value={fNumber(item.amount_per_kg, { disabledDecimal: true })}
                    unit={sizeUnit}
                  />
                  <Stat
                    area="cost"
                    icon={<TransferIcon name="wallet" size={2} />}
                    label={t('transfer.costShort')}
                    value={fNumber(item.cost_per_kg)}
                    unit={costUnit}
                  />
                  <Box sx={{ gridArea: 'weight', textAlign: 'right' }}>
                    <Typography
                      fontWeight={700}
                      component="span"
                      sx={{ typography: { xs: 'h6', md: 'h5' } }}
                    >
                      {fNumber(item.amount, { disabledDecimal: true })}
                    </Typography>{' '}
                    <Typography variant="body2" color="text.secondary" component="span">
                      {t('transfer.kg')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t('transfer.quantity')}
                    </Typography>
                  </Box>
                  {!isMobile && (
                    <ChevronRightIcon sx={{ gridArea: 'chevron', color: 'text.secondary' }} />
                  )}
                </ButtonBase>
                <Divider />
              </Box>
            )
          })}
          <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
            {t('transfer.showing', { shown: data.length, total: data.length })}
          </Typography>
        </Box>
      )}
      <ShrimpTransferViewDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        data={selectedRecord as PartialTransfer}
        onDelete={handleOpenDeleteConfirm}
      />
      {dialog}
    </Stack>
  )
}

export default ShrimpTransfersRecordBody
